import { useState, useRef, useCallback, useEffect } from 'react';
import { isSafari } from '../util/browserDetection';
import { detectLanguageFromText } from '../util/helper';

const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

// Safari only supports limited languages — Bengali is NOT supported.
// Fallback to en-US on Safari regardless of app language.
const getSpeechLang = (language) => {
    if (isSafari()) return 'en-US';
    return language === 'bn' ? 'bn-IN' : 'en-US';
};

const SILENCE_TIMEOUT = 1500; // Auto-stop after 1.5s of silence once user has spoken
const NO_SPEECH_TIMEOUT = 5000; // Auto-close if no speech detected for 5s

const useVoiceRecognition = (language = 'en') => {
    const [isListening, setIsListening] = useState(false);
    const [transcript, setTranscript] = useState('');
    const [finalTranscript, setFinalTranscript] = useState('');
    const [detectedLanguage, setDetectedLanguage] = useState(language);
    const [error, setError] = useState(null);
    const recognitionRef = useRef(null);
    const manualStopRef = useRef(false);
    const silenceTimerRef = useRef(null);
    const noSpeechTimerRef = useRef(null);
    const hasSpokenRef = useRef(false);
    const onAutoStopRef = useRef(null); // callback when auto-stop fires
    const isSupported = !!SpeechRecognition;

    // Update detected language when transcript changes
    useEffect(() => {
        if (transcript) {
            const detected = detectLanguageFromText(transcript, language);
            setDetectedLanguage(detected);
        }
    }, [transcript, language]);

    useEffect(() => {
        return () => {
            manualStopRef.current = true;
            clearSilenceTimer();
            clearNoSpeechTimer();
            if (recognitionRef.current) {
                try {
                    recognitionRef.current.abort();
                } catch (e) {
                    /* */
                }
            }
        };
    }, []);

    const clearSilenceTimer = () => {
        if (silenceTimerRef.current) {
            clearTimeout(silenceTimerRef.current);
            silenceTimerRef.current = null;
        }
    };

    const clearNoSpeechTimer = () => {
        if (noSpeechTimerRef.current) {
            clearTimeout(noSpeechTimerRef.current);
            noSpeechTimerRef.current = null;
        }
    };

    const resetSilenceTimer = useCallback(() => {
        clearSilenceTimer();
        // Only start silence timer if user has already spoken something
        if (hasSpokenRef.current) {
            silenceTimerRef.current = setTimeout(() => {
                // Auto-stop: user went silent for 2s after speaking
                manualStopRef.current = true;
                try {
                    recognitionRef.current.stop();
                } catch (e) {
                    /* */
                }
                setIsListening(false);
                // Fire the callback once, then clear it to prevent double-firing
                const cb = onAutoStopRef.current;
                onAutoStopRef.current = null;
                if (cb) cb();
            }, SILENCE_TIMEOUT);
        }
    }, []);

    const startListening = useCallback(
        (onAutoStop) => {
            if (!SpeechRecognition) {
                setError('Speech recognition not supported');
                return;
            }
            if (recognitionRef.current) {
                try {
                    recognitionRef.current.abort();
                } catch (e) {
                    /* */
                }
            }

            setTranscript('');
            setFinalTranscript('');
            setError(null);
            manualStopRef.current = false;
            hasSpokenRef.current = false;
            onAutoStopRef.current = onAutoStop || null;

            const recognition = new SpeechRecognition();
            recognitionRef.current = recognition;
            recognition.lang = getSpeechLang(language);
            recognition.continuous = true;
            recognition.interimResults = true;
            recognition.maxAlternatives = 1;

            recognition.onstart = () => {
                setIsListening(true);
                // Start no-speech timeout — if nothing spoken in 6s, auto-close
                clearNoSpeechTimer();
                // The timer is cleared as soon as the user speaks, so reaching it means silence
                noSpeechTimerRef.current = setTimeout(() => {
                    manualStopRef.current = true;
                    try {
                        recognitionRef.current.stop();
                    } catch (e) {
                        /* */
                    }
                    setIsListening(false);
                    setError('no-speech');
                }, NO_SPEECH_TIMEOUT);
            };

            recognition.onresult = (event) => {
                // If already manually stopped (e.g. silence timer fired), ignore late results
                // This prevents a second silence timer from being created by late browser results
                if (manualStopRef.current) return;

                let interim = '';
                let final = '';
                for (let i = 0; i < event.results.length; i++) {
                    const result = event.results[i];
                    if (result.isFinal) {
                        final += result[0].transcript;
                    } else {
                        interim += result[0].transcript;
                    }
                }

                // User has spoken — clear the no-speech timeout
                if (final || interim) {
                    hasSpokenRef.current = true;
                    clearNoSpeechTimer();
                }

                setTranscript(final || interim);
                if (final) setFinalTranscript(final);

                // Reset silence timer on every speech result (user is still talking)
                resetSilenceTimer();
            };

            recognition.onerror = (event) => {
                if (event.error === 'aborted') return;
                if (event.error === 'no-speech' && !manualStopRef.current) {
                    return;
                }
                manualStopRef.current = true;
                clearSilenceTimer();
                clearNoSpeechTimer();
                setError(event.error);
                setIsListening(false);
            };

            recognition.onend = () => {
                if (!manualStopRef.current && !error) {
                    try {
                        recognition.start();
                        return;
                    } catch (e) {
                        /* fall through */
                    }
                }
                setIsListening(false);
            };

            try {
                recognition.start();
            } catch (e) {
                setError('Failed to start recognition');
                setIsListening(false);
            }
        },
        [language, error, resetSilenceTimer]
    );

    const stopListening = useCallback(() => {
        manualStopRef.current = true;
        clearSilenceTimer();
        clearNoSpeechTimer();
        if (recognitionRef.current) {
            try {
                recognitionRef.current.stop();
            } catch (e) {
                /* */
            }
        }
        setIsListening(false);
    }, []);

    const resetTranscript = useCallback(() => {
        setTranscript('');
        setFinalTranscript('');
        setError(null);
    }, []);

    return {
        isListening,
        transcript,
        finalTranscript,
        detectedLanguage,
        error,
        isSupported,
        isSafariFallback: isSafari() && language === 'bn',
        startListening,
        stopListening,
        resetTranscript,
    };
};

export default useVoiceRecognition;
